import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/auth/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { BusinessError } from '@/api/types';
import * as splashApi from '@/api/splash';
import type { StoredUser } from '@/auth/storage';
import { glass as glassPresets } from '@/styles/glass';
import { HERO_BG } from '@/constants/images';

/* ─── 兜底诗词（后端拿不到时用）─── */
const FALLBACK_POEM = {
  lines: ['落霞与孤鹜齐飞，', '秋水共长天一色。'],
  author: '王勃',
  source: '《滕王阁序》',
};

/* ─── 兜底背景图 ─── */
const FALLBACK_SPLASH_BG = HERO_BG.splash;
const FALLBACK_LOGIN_BG = HERO_BG.login;

const glass = glassPresets.panel;

const inputStyle = {
  background: 'rgba(252,250,247,0.80)',
  border: '0.5px solid rgba(210,200,190,0.55)',
} as const;

/* ─── 诗词过渡页 ─── */
function PoetryTransition({ onDone }: { onDone: () => void }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // 每日一句（按日缓存）
  const dailyQ = useQuery({
    queryKey: ['splash', 'daily-quote', new Date().toDateString()],
    queryFn: splashApi.getDailyQuote,
    staleTime: 12 * 60 * 60 * 1000,  // 12 小时
    retry: 0,  // splash 拿不到就用 fallback，不需要重试
  });
  const poem = dailyQ.data
    ? { lines: dailyQ.data.lines, author: dailyQ.data.author, source: dailyQ.data.source }
    : FALLBACK_POEM;

  // splash 背景图（随机挑一张）
  const bgQ = useQuery({
    queryKey: ['splash', 'login-bg'],
    queryFn: splashApi.listLoginBackgrounds,
    staleTime: Infinity,
    retry: 0,
  });
  const splashBg = useMemo(() => {
    const items = bgQ.data?.items ?? [];
    if (items.length === 0) return FALLBACK_SPLASH_BG;
    return items[Math.floor(Math.random() * items.length)];
  }, [bgQ.data]);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    const t2 = setTimeout(() => handleLeave(), 3800);
    return () => { clearTimeout(t); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLeave = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    // 立即跳转：过渡页是叠在登录页之上的浮层，若先淡出再跳转，
    // 淡出的 0.7s 里下方登录页会透出来形成"闪一下账号登录页"。
    // 目标页 chunk 已在动画期间预加载，这里直接 navigate 即可无缝切换。
    onDone();
  }, [leaving, onDone]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden cursor-pointer"
      onClick={handleLeave}
      style={{
        opacity: leaving ? 0 : visible ? 1 : 0,
        transition: leaving ? 'opacity 0.7s ease' : 'opacity 0.9s ease',
      }}
    >
      {/* 背景图（来自 /splash/login-backgrounds，或用 fallback）*/}
      <img
        src={splashBg}
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      {/* 渐变蒙层 */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, rgba(60,30,60,0.38) 0%, rgba(20,10,30,0.18) 50%, rgba(0,0,0,0.25) 100%)' }} />

      {/* 诗词内容 - 竖排 */}
      <div
        className="relative z-10 flex flex-col items-center"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          transition: 'all 1.1s cubic-bezier(0.22,1,0.36,1) 0.2s',
        }}
      >
        {/* 竖排诗句 */}
        <div className="flex flex-row-reverse gap-6 mb-10">
          {poem.lines.map((line, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-1"
              style={{
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(30px)',
                transition: `all 1.1s cubic-bezier(0.22,1,0.36,1) ${0.3 + i * 0.18}s`,
              }}
            >
              <span
                style={{
                  fontFamily: '"Noto Serif SC", "Songti SC", serif',
                  fontSize: '2rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.95)',
                  textShadow: '0 2px 24px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.3)',
                  lineHeight: 1.35,
                  letterSpacing: '0.08em',
                }}
              >
                {line}
              </span>
            </div>
          ))}
        </div>

        {/* 作者来源 - 竖排 */}
        <div
          className="flex flex-col items-center gap-0.5"
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            opacity: visible ? 0.82 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 1.1s cubic-bezier(0.22,1,0.36,1) 0.7s',
            alignSelf: 'flex-end',
            marginRight: '24px',
          }}
        >
          <span style={{ fontFamily: '"Noto Serif SC", serif', fontSize: '0.9rem', color: 'rgba(255,248,235,0.88)', letterSpacing: '0.12em', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
            ——{poem.author}
          </span>
          <span style={{ fontFamily: '"Noto Serif SC", serif', fontSize: '0.85rem', color: 'rgba(255,248,235,0.72)', letterSpacing: '0.1em', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
            {poem.source}
          </span>
        </div>
      </div>

      {/* 点击提示 */}
      <div
        className="absolute bottom-14 left-0 right-0 flex justify-center"
        style={{
          opacity: visible ? 0.55 : 0,
          transition: 'opacity 1s ease 1.2s',
        }}
      >
        <span style={{ fontFamily: '"Noto Serif SC", serif', fontSize: '0.75rem', color: 'rgba(255,248,235,0.8)', letterSpacing: '0.2em' }}>
          轻触屏幕继续
        </span>
      </div>
    </div>
  );
}

/* ─── 按角色跳转 ─── */
function redirectForRole(role: StoredUser['role']): string {
  switch (role) {
    case 'admin': return '/admin';
    case 'teacher': return '/teacher';
    case 'student':
    case 'assistant':
    default: return '/';
  }
}

/* ─── 预加载目标页 chunk ───
   过渡动画期间就把目标页的懒加载产物拉好，跳转后直接渲染，
   既不会闪登录页、也不会闪 Suspense 加载态。 */
function preloadForRole(role: StoredUser['role']): void {
  switch (role) {
    case 'admin': import('../admin/page'); break;
    case 'teacher': import('../teacher/page'); break;
    default: import('../home/page'); break;
  }
}

/* ─── 登录主页面 ─── */
export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showToast } = useToast();
  const [loginType, setLoginType] = useState<'phone' | 'studentId'>('phone');
  const [phone, setPhone] = useState('');
  const [studentId, setStudentId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showPoetry, setShowPoetry] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [redirectTo, setRedirectTo] = useState('/');

  // 复用启动页的背景图池（同一个 queryKey 全局只请求一次）
  const bgQ = useQuery({
    queryKey: ['splash', 'login-bg'],
    queryFn: splashApi.listLoginBackgrounds,
    staleTime: Infinity,
    retry: 0,
  });
  const loginBg = useMemo(() => {
    const items = bgQ.data?.items ?? [];
    if (items.length === 0) return FALLBACK_LOGIN_BG;
    return items[Math.floor(Math.random() * items.length)];
  }, [bgQ.data]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleLogin = async () => {
    if (!canSubmit) return;
    setErrorMsg('');
    setLoading(true);
    const account = loginType === 'phone' ? phone : studentId;
    try {
      const { user, mustChangePassword } = await login(account, password, loginType);
      if (mustChangePassword) {
        showToast({ message: '请尽快修改初始密码', type: 'info' });
      }
      setRedirectTo(redirectForRole(user.role));
      preloadForRole(user.role);
      setShowPoetry(true);
    } catch (err) {
      if (err instanceof BusinessError) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg('登录失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePoetryDone = useCallback(() => {
    navigate(redirectTo);
  }, [navigate, redirectTo]);

  const accountValid = loginType === 'phone' ? phone.length >= 11 : studentId.length >= 4;
  const canSubmit = agreed && accountValid && password.length >= 6;

  return (
    <>
      {showPoetry && <PoetryTransition onDone={handlePoetryDone} />}

      <div className="h-screen flex flex-col relative overflow-hidden">
        {/* ── 背景图 ──（覆盖整屏，PC 也有视觉）*/}
        <div className="absolute inset-0 z-0">
          <img
            src={loginBg}
            alt=""
            className="w-full h-full object-cover object-top"
          />
          {/* 渐变遮罩：移动端上浅下深，桌面端整体加深一点让居中卡片浮出来 */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(240,245,255,0.30) 0%, rgba(240,245,255,0.05) 38%, rgba(248,250,255,0.45) 75%, rgba(248,250,255,0.80) 100%)' }} />
          <div className="absolute inset-0 hidden md:block" style={{ background: 'radial-gradient(ellipse at center, rgba(240,245,255,0.25) 0%, rgba(180,195,220,0.35) 100%)' }} />
        </div>

        {/* ── Logo 区 ──（只在手机端显示；PC 端 logo 搬进卡片左栏）*/}
        <div
          className="relative z-10 flex flex-col items-center pt-16 pb-4 md:hidden"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(-20px)',
            transition: 'all 0.8s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <h1
            className="text-[1.75rem] font-semibold tracking-widest mb-0.5"
            style={{ color: '#2A3A5C', fontFamily: '"Noto Serif SC", serif', textShadow: '0 1px 8px rgba(255,255,255,0.8)' }}
          >
            诗词大先生
          </h1>
          <p
            className="text-xs tracking-widest"
            style={{ color: 'rgba(60,80,120,0.72)', fontFamily: '"Noto Serif SC", serif' }}
          >
            诗词启智 · 以文化人
          </p>
        </div>

        {/* ── 登录卡片区 ──
            移动：justify-end 贴底 + 单栏；PC（md+）：justify-center 居中 + 4:3 双栏（左品牌 / 右表单）*/}
        <div className="relative z-10 flex-1 flex flex-col justify-end md:justify-center items-center px-5 pb-9 md:pb-12">
          <div
            className="rounded-3xl w-full md:max-w-[760px] lg:max-w-[880px] flex flex-col md:flex-row overflow-hidden md:min-h-[560px] lg:min-h-[640px]"
            style={{
              ...glass,
              boxShadow: '0 20px 60px rgba(44,40,52,0.13), inset 0 1.5px 0 rgba(255,255,255,0.92)',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(32px)',
              transition: 'all 0.8s cubic-bezier(0.22,1,0.36,1) 0.12s',
            }}
          >
            {/* ── PC 左栏：品牌区 ─── */}
            <div
              className="hidden md:flex md:w-[42%] lg:w-[44%] flex-col justify-center items-center px-8 lg:px-10 py-10 relative"
              style={{
                background: 'linear-gradient(160deg, rgba(42,58,92,0.08) 0%, rgba(200,116,90,0.06) 100%)',
                borderRight: '0.5px solid rgba(210,200,190,0.4)',
              }}
            >
              {/* 装饰竖线 */}
              <div
                className="absolute top-10 bottom-10 left-6 lg:left-8 w-[2px] rounded-full"
                style={{ background: 'linear-gradient(to bottom, rgba(200,116,90,0.5), rgba(200,116,90,0))' }}
              />

              <div className="flex flex-col items-center text-center">
                <h1
                  className="text-4xl lg:text-5xl font-semibold tracking-[0.18em] leading-tight mb-3 whitespace-nowrap"
                  style={{ color: '#2A3A5C', fontFamily: '"Noto Serif SC", serif', textShadow: '0 1px 8px rgba(255,255,255,0.85)' }}
                >
                  诗词大先生
                </h1>
                <p
                  className="text-sm lg:text-base tracking-[0.15em] mb-8"
                  style={{ color: 'rgba(60,80,120,0.78)', fontFamily: '"Noto Serif SC", serif' }}
                >
                  诗词启智 · 以文化人
                </p>
                <div
                  className="w-14 h-[1.5px] rounded-full mb-6"
                  style={{ background: 'rgba(200,116,90,0.5)' }}
                />
                <p
                  className="text-sm lg:text-base leading-loose tracking-[0.08em]"
                  style={{ color: 'rgba(90,80,75,0.72)', fontFamily: '"Noto Serif SC", serif' }}
                >
                  以先贤之诗<br/>
                  启今人之心
                </p>
              </div>
            </div>

            {/* ── 右栏（PC）/ 整卡（手机）：表单 ── */}
            <div className="flex-1 p-5 md:p-8 lg:p-10">

            {/* 标题 */}
            <p className="text-base md:text-xl lg:text-2xl font-semibold mb-4 md:mb-6 text-center" style={{ color: '#3A2C28', fontFamily: '"Noto Serif SC", serif', letterSpacing: '0.06em' }}>
              账号登录
            </p>

            {/* 登录方式切换 */}
            <div className="flex rounded-xl p-1 mb-4 md:mb-5" style={{ background: 'rgba(210,200,190,0.22)', border: '0.5px solid rgba(210,200,190,0.4)' }}>
              {([
                { key: 'phone', label: '手机号登录' },
                { key: 'studentId', label: '学号 / 工号' },
              ] as const).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setLoginType(tab.key)}
                  className="flex-1 py-2 md:py-3 rounded-lg text-xs md:text-base font-medium cursor-pointer transition-all whitespace-nowrap"
                  style={loginType === tab.key
                    ? { background: 'rgba(255,255,255,0.92)', color: '#3A2C28', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', fontFamily: '"Noto Serif SC", serif' }
                    : { color: 'rgba(140,120,110,0.7)', fontFamily: '"Noto Serif SC", serif' }
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 手机号输入 */}
            {loginType === 'phone' && (
              <div className="mb-3 md:mb-4">
                <div className="flex items-center rounded-2xl px-4 md:px-5 h-12 md:h-[60px] gap-3" style={inputStyle}>
                  <div className="flex items-center gap-1.5 pr-3 flex-shrink-0" style={{ borderRight: '1px solid rgba(180,170,160,0.45)' }}>
                    <span className="text-sm font-medium" style={{ color: '#6A5A50', fontFamily: '"Noto Serif SC", serif' }}>+86</span>
                    <i className="ri-arrow-down-s-line text-sm" style={{ color: '#B4A49A' }} />
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={11}
                    value={phone}
                    onChange={e => { setPhone(e.target.value.replace(/\D/g, '')); setErrorMsg(''); }}
                    placeholder="请输入手机号"
                    className="flex-1 bg-transparent outline-none text-sm md:text-base lg:text-[17px] login-input"
                    style={{ color: '#3A2C28', fontFamily: '"Noto Serif SC", serif' }}
                  />
                  {phone.length > 0 && (
                    <button onClick={() => setPhone('')} className="w-5 h-5 flex items-center justify-center cursor-pointer flex-shrink-0">
                      <i className="ri-close-circle-fill text-base" style={{ color: '#C4B4AA' }} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* 学号 / 工号输入 */}
            {loginType === 'studentId' && (
              <div className="mb-3 md:mb-4">
                <div className="flex items-center rounded-2xl px-4 md:px-5 h-12 md:h-[60px] gap-3" style={inputStyle}>
                  <div className="flex items-center gap-1.5 pr-3 flex-shrink-0" style={{ borderRight: '1px solid rgba(180,170,160,0.45)' }}>
                    <i className="ri-id-card-line text-base" style={{ color: '#B4A49A' }} />
                  </div>
                  <input
                    type="text"
                    maxLength={20}
                    value={studentId}
                    onChange={e => { setStudentId(e.target.value); setErrorMsg(''); }}
                    placeholder="请输入学号或教职工号"
                    className="flex-1 bg-transparent outline-none text-sm md:text-base lg:text-[17px] login-input"
                    style={{ color: '#3A2C28', fontFamily: '"Noto Serif SC", serif' }}
                  />
                  {studentId.length > 0 && (
                    <button onClick={() => setStudentId('')} className="w-5 h-5 flex items-center justify-center cursor-pointer flex-shrink-0">
                      <i className="ri-close-circle-fill text-base" style={{ color: '#C4B4AA' }} />
                    </button>
                  )}
                </div>
                <p className="text-xs mt-1.5 px-1" style={{ color: 'rgba(160,145,135,0.75)', fontFamily: '"Noto Serif SC", serif' }}>
                  学生请输入学号，教师请输入教职工号
                </p>
              </div>
            )}

            {/* 密码输入 */}
            <div className="mb-4 md:mb-5">
              <div className="flex items-center rounded-2xl px-4 h-12 md:h-14 gap-3" style={inputStyle}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setErrorMsg(''); }}
                  placeholder="请输入密码"
                  className="flex-1 bg-transparent outline-none text-sm md:text-base lg:text-[17px]"
                  style={{ color: '#3A2C28', fontFamily: '"Noto Serif SC", serif' }}
                />
                <button onClick={() => setShowPassword(v => !v)} className="w-5 h-5 flex items-center justify-center cursor-pointer flex-shrink-0">
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base md:text-lg`} style={{ color: '#C4B4AA' }} />
                </button>
              </div>
              <div className="flex justify-end mt-2">
                <button className="text-xs md:text-sm cursor-pointer whitespace-nowrap" style={{ color: '#C8745A', fontFamily: '"Noto Serif SC", serif' }}>
                  忘记密码？
                </button>
              </div>
            </div>

            {/* 演示账号提示 */}
            <div className="mb-4 md:mb-5 px-3 md:px-4 py-2.5 md:py-3 rounded-xl" style={{ background: 'rgba(210,200,190,0.18)', border: '0.5px solid rgba(210,200,190,0.4)' }}>
              <p className="text-xs md:text-sm mb-1.5 md:mb-2 font-medium" style={{ color: '#8A7A6A', fontFamily: '"Noto Serif SC", serif' }}>演示账号</p>
              <div className="space-y-1 md:space-y-1.5">
                {[
                  { label: '管理员', account: '10000', pwd: 'admin123', color: '#B8915A', type: 'studentId' as const },
                  { label: '教师', account: '13800138001', pwd: 'teacher123', color: '#6E8FAE', type: 'phone' as const },
                  { label: '学生', account: '13800138000', pwd: '123456', color: '#6B9E6B', type: 'phone' as const },
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={() => {
                      setErrorMsg('');
                      setLoginType(item.type);
                      if (item.type === 'phone') setPhone(item.account);
                      else setStudentId(item.account);
                      setPassword(item.pwd);
                      setAgreed(true);
                    }}
                    className="w-full flex items-center justify-between cursor-pointer rounded-lg px-2 md:px-3 py-1 md:py-1.5 transition-all text-left"
                    style={{ background: 'transparent' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.5)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span className="text-xs md:text-sm font-medium" style={{ color: item.color, fontFamily: '"Noto Serif SC", serif' }}>{item.label}</span>
                    <span className="text-xs md:text-sm" style={{ color: '#A09080', fontFamily: 'monospace' }}>{item.account} / {item.pwd}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 错误提示 */}
            {errorMsg && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(218,88,64,0.08)', border: '0.5px solid rgba(218,88,64,0.25)' }}>
                <i className="ri-error-warning-line text-sm flex-shrink-0" style={{ color: '#DA5840' }} />
                <span className="text-xs" style={{ color: '#DA5840', fontFamily: '"Noto Serif SC", serif' }}>{errorMsg}</span>
              </div>
            )}

            {/* 登录按钮 */}
            <button
              onClick={handleLogin}
              disabled={!canSubmit || loading}
              className="w-full py-3.5 md:py-4 lg:py-4 rounded-2xl text-base md:text-lg lg:text-xl font-semibold transition-all active:scale-[0.97] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              style={canSubmit
                ? { background: 'linear-gradient(135deg, #DA5840 0%, #B83824 100%)', color: '#fff', boxShadow: '0 6px 22px rgba(186,52,32,0.36)', fontFamily: '"Noto Serif SC", serif' }
                : { background: 'rgba(200,190,185,0.32)', color: 'rgba(140,120,110,0.55)', fontFamily: '"Noto Serif SC", serif' }}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full" style={{ animation: 'spin 0.7s linear infinite' }} />
                  <span>正在登录...</span>
                </>
              ) : '登 录'}
            </button>

            {/* 协议 */}
            <div className="flex items-start gap-2 mt-4 md:mt-5">
              <button
                onClick={() => setAgreed(v => !v)}
                className="cursor-pointer transition-all flex-shrink-0 mt-0.5"
                style={agreed
                  ? { background: 'linear-gradient(135deg, #DA5840, #B83824)', border: '1.5px solid transparent', width: 18, height: 18, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }
                  : { background: 'transparent', border: '1.5px solid rgba(180,165,155,0.6)', width: 18, height: 18, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {agreed && <i className="ri-check-line text-white" style={{ fontSize: '10px' }} />}
              </button>
              <p className="text-xs md:text-sm leading-relaxed" style={{ color: '#9A8A7A', fontFamily: '"Noto Serif SC", serif' }}>
                我已阅读并同意
                <span className="cursor-pointer" style={{ color: '#C8745A' }}>《用户协议》</span>
                与
                <span className="cursor-pointer" style={{ color: '#C8745A' }}>《隐私政策》</span>
              </p>
            </div>
            </div> {/* /右栏 */}
          </div>

          {/* 注册引导 */}
          <p
            className="text-xs md:text-sm text-center mt-5 md:mt-6"
            style={{
              color: '#B4A49A',
              fontFamily: '"Noto Serif SC", serif',
              opacity: mounted ? 1 : 0,
              transition: 'opacity 0.8s ease 0.3s',
            }}
          >
            还没有账号？
            <span className="cursor-pointer font-medium" style={{ color: '#C8745A' }}>立即注册</span>
          </p>
        </div>

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          .login-input::placeholder {
            color: rgba(160, 145, 135, 0.75);
            font-family: 'Noto Serif SC', serif;
          }
          .login-input::-webkit-input-placeholder {
            color: rgba(160, 145, 135, 0.75);
          }
          .login-input::-moz-placeholder {
            color: rgba(160, 145, 135, 0.75);
          }
          input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0px 1000px rgba(252, 250, 247, 0.95) inset !important;
            -webkit-text-fill-color: #3A2C28 !important;
            transition: background-color 5000s ease-in-out 0s;
            font-family: 'Noto Serif SC', serif !important;
          }
        `}</style>
      </div>
    </>
  );
}
